// Formato binário de ghost (docs/05): pos+yaw+pitch a 128/6 ≈ 21.3 Hz + splits.
// Gzip por cima (CompressionStream — existe no browser E no Node 18+).
import { fnv1a } from './rng';
import { GHOST_SAMPLE_DIV, PHYSICS_VERSION } from './constants';

export const GHOST_MAGIC = 0x53474831; // "SGH1"
export const GHOST_FLOATS_PER_SAMPLE = 5; // x, y, z, yaw, pitch
export const GHOST_MAX_RAW_BYTES = 2_000_000;

export interface GhostData {
  physicsVersion: number;
  timeMs: number;
  slugHash: number;
  sampleCount: number;
  /** sampleCount × 5 floats (x,y,z,yaw,pitch) */
  samples: Float32Array;
  /** tempos de entrada nos stages 2..N (ms) */
  splits: number[];
}

export function encodeGhost(g: Omit<GhostData, 'physicsVersion'>): Uint8Array {
  const headerBytes = 24;
  const sampleBytes = g.sampleCount * GHOST_FLOATS_PER_SAMPLE * 4;
  const splitBytes = 2 + g.splits.length * 4;
  const buf = new ArrayBuffer(headerBytes + sampleBytes + splitBytes);
  const dv = new DataView(buf);
  dv.setUint32(0, GHOST_MAGIC, true);
  dv.setUint16(4, PHYSICS_VERSION, true);
  dv.setUint16(6, GHOST_SAMPLE_DIV, true);
  dv.setUint32(8, g.timeMs, true);
  dv.setUint32(12, g.sampleCount, true);
  dv.setUint32(16, g.slugHash, true);
  dv.setUint32(20, 0, true); // reservado
  new Float32Array(buf, headerBytes, g.sampleCount * GHOST_FLOATS_PER_SAMPLE)
    .set(g.samples.subarray(0, g.sampleCount * GHOST_FLOATS_PER_SAMPLE));
  const off = headerBytes + sampleBytes;
  dv.setUint16(off, g.splits.length, true);
  g.splits.forEach((s, i) => dv.setUint32(off + 2 + i * 4, s, true));
  return new Uint8Array(buf);
}

export function decodeGhost(bytes: Uint8Array): GhostData | null {
  if (bytes.byteLength < 26 || bytes.byteLength > GHOST_MAX_RAW_BYTES) return null;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (dv.getUint32(0, true) !== GHOST_MAGIC) return null;
  const physicsVersion = dv.getUint16(4, true);
  const sampleDiv = dv.getUint16(6, true);
  const timeMs = dv.getUint32(8, true);
  const sampleCount = dv.getUint32(12, true);
  const slugHash = dv.getUint32(16, true);
  const sampleBytes = sampleCount * GHOST_FLOATS_PER_SAMPLE * 4;
  if (sampleDiv !== GHOST_SAMPLE_DIV) return null;
  if (24 + sampleBytes + 2 > bytes.byteLength) return null;
  // Float32Array exige offset múltiplo de 4 — copia para buffer próprio
  const samples = new Float32Array(sampleCount * GHOST_FLOATS_PER_SAMPLE);
  for (let i = 0; i < samples.length; i++) samples[i] = dv.getFloat32(24 + i * 4, true);
  const off = 24 + sampleBytes;
  const splitCount = dv.getUint16(off, true);
  if (off + 2 + splitCount * 4 > bytes.byteLength || splitCount > 64) return null;
  const splits: number[] = [];
  for (let i = 0; i < splitCount; i++) splits.push(dv.getUint32(off + 2 + i * 4, true));
  return { physicsVersion, timeMs, slugHash, sampleCount, samples, splits };
}

/** Sanidade do ghost vs. a run declarada (server e client). */
export function ghostMatchesRun(g: GhostData, slug: string, timeMs: number): boolean {
  if (g.slugHash !== fnv1a(slug)) return false;
  if (g.timeMs !== timeMs) return false;
  const expected = Math.floor(timeMs / 1000 * (128 / GHOST_SAMPLE_DIV));
  return Math.abs(g.sampleCount - expected) <= 4;
}

// --- gzip helpers (browser + node 18+) ---
async function pump(input: Uint8Array, stream: { readable: ReadableStream; writable: WritableStream }): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  void writer.write(input);
  void writer.close();
  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
  }
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.byteLength; }
  return out;
}

export function gzipBytes(data: Uint8Array): Promise<Uint8Array> {
  return pump(data, new CompressionStream('gzip'));
}

export function gunzipBytes(data: Uint8Array): Promise<Uint8Array> {
  return pump(data, new DecompressionStream('gzip'));
}

export function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 8192) s += String.fromCharCode(...b.subarray(i, i + 8192));
  return btoa(s);
}

export function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
